import { useEffect, useState } from "react";
import api, { apiError, downloadUrl } from "@/lib/api";
import { Modal } from "@/components/ui/bits";
import { fmtDate } from "@/lib/constants";
import {
  Plus, Download, Shield, KeyRound, Trash2, UserCog, ScrollText, Users as UsersIcon,
} from "lucide-react";
import { toast } from "sonner";

export default function Practice() {
  const [tab, setTab] = useState("Staff");
  const tabs = ["Staff", "Users", "Audit", "Exports"];
  return (
    <div>
      <h1 className="font-serif font-bold text-3xl text-white mb-1">Practice</h1>
      <p className="text-slate-400 mb-5">Team, access and records.</p>
      <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-6">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`practicetab-${t.toLowerCase()}`}
            className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm border ${tab === t ? "bg-[#C9A84A] text-[#0A0F1F] border-[#C9A84A] font-semibold" : "card-surface text-slate-300 border-[#1E294B]"}`}>{t}</button>
        ))}
      </div>
      {tab === "Staff" && <StaffTab />}
      {tab === "Users" && <UsersTab />}
      {tab === "Audit" && <AuditTab />}
      {tab === "Exports" && <ExportsTab />}
    </div>
  );
}

const field = "w-full bg-[#0A0F1F] border border-[#1E294B] rounded-xl px-4 py-3 text-white outline-none focus:border-[#C9A84A]/60";

function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [add, setAdd] = useState({ name: "", email: "", temp_password: "" });
  const [assignFor, setAssignFor] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = () => { api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {}); api.get("/clients").then(({ data }) => setClients(data.filter(Boolean))).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const createStaff = async (e) => { e.preventDefault(); try { await api.post("/staff", add); toast.success("Staff added"); setShowAdd(false); setAdd({ name: "", email: "", temp_password: "" }); load(); } catch (e) { toast.error(apiError(e)); } };
  const openAssign = (s) => { setAssignFor(s); setSelected((s.assigned_clients || []).map((c) => c.id)); };
  const saveAssign = async () => { try { await api.put(`/staff/${assignFor.id}/assign`, { client_ids: selected }); toast.success("Assignments saved"); setAssignFor(null); load(); } catch (e) { toast.error(apiError(e)); } };
  const reset2fa = async (id) => { try { await api.post(`/admin/users/${id}/reset-2fa`); toast.success("2FA reset — they'll set it up at next sign-in"); load(); } catch (e) { toast.error(apiError(e)); } };
  const resetPw = async (id) => { const pw = prompt("New temporary password (min 8 chars):"); if (!pw) return; try { await api.post(`/admin/users/${id}/reset-password`, { new_password: pw }); toast.success("Password reset"); } catch (e) { toast.error(apiError(e)); } };
  const delStaff = async (id) => { if (!window.confirm("Remove this staff member?")) return; try { await api.delete(`/staff/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-4">
      <button onClick={() => setShowAdd(true)} className="bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-full px-5 py-2.5 flex items-center gap-2" data-testid="add-staff"><Plus size={16} /> Add staff</button>
      {staff.length === 0 && <p className="text-slate-500 text-sm">No staff yet.</p>}
      {staff.map((s) => (
        <div key={s.id} className="card-surface rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium">{s.name}</div>
              <div className="text-slate-500 text-sm">{s.email}</div>
              <div className="text-slate-500 text-xs mt-1">{(s.assigned_clients || []).length} clients · 2FA {s.totp_enabled ? "on" : "not set"}</div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap mt-3">
            <button onClick={() => openAssign(s)} className="text-xs card-surface border border-[#1E294B] rounded-full px-3 py-1.5 text-white flex items-center gap-1"><UserCog size={14} /> Assign clients</button>
            <button onClick={() => reset2fa(s.id)} className="text-xs card-surface border border-[#1E294B] rounded-full px-3 py-1.5 text-white flex items-center gap-1"><Shield size={14} /> Reset 2FA</button>
            <button onClick={() => resetPw(s.id)} className="text-xs card-surface border border-[#1E294B] rounded-full px-3 py-1.5 text-white flex items-center gap-1"><KeyRound size={14} /> Reset password</button>
            <button onClick={() => delStaff(s.id)} className="text-xs border border-red-500/40 rounded-full px-3 py-1.5 text-red-300 flex items-center gap-1"><Trash2 size={14} /> Remove</button>
          </div>
        </div>
      ))}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add staff member">
        <form onSubmit={createStaff} className="space-y-3">
          <input className={field} placeholder="Full name" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} required data-testid="staff-name" />
          <input type="email" className={field} placeholder="Email" value={add.email} onChange={(e) => setAdd({ ...add, email: e.target.value })} required data-testid="staff-email" />
          <input className={field} placeholder="Temporary password (min 8)" minLength={8} value={add.temp_password} onChange={(e) => setAdd({ ...add, temp_password: e.target.value })} required data-testid="staff-password" />
          <p className="text-slate-500 text-xs">They must change the password and set up 2FA on first sign-in.</p>
          <button className="w-full bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-xl py-3" data-testid="staff-submit">Add staff</button>
        </form>
      </Modal>

      <Modal open={!!assignFor} onClose={() => setAssignFor(null)} title={`Assign clients to ${assignFor?.name || ""}`}>
        <div className="max-h-80 overflow-y-auto space-y-1">
          {clients.length === 0 && <p className="text-slate-500 text-sm">No clients to assign.</p>}
          {clients.map((c) => (
            <label key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer">
              <input type="checkbox" checked={selected.includes(c.id)} onChange={(e) => setSelected((s) => e.target.checked ? [...s, c.id] : s.filter((x) => x !== c.id))} className="w-4 h-4 accent-[#C9A84A]" />
              <span className="text-white text-sm">{c.name}</span>
            </label>
          ))}
        </div>
        <button onClick={saveAssign} className="w-full mt-4 bg-[#C9A84A] text-[#0A0F1F] font-bold rounded-xl py-3">Save assignments</button>
      </Modal>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const reset2fa = async (id) => { try { await api.post(`/admin/users/${id}/reset-2fa`); toast.success("2FA reset"); load(); } catch (e) { toast.error(apiError(e)); } };
  const resetPw = async (id) => { const pw = prompt("New temporary password (min 8 chars):"); if (!pw) return; try { await api.post(`/admin/users/${id}/reset-password`, { new_password: pw }); toast.success("Password reset"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-2">
      {users.map((u) => (
        <div key={u.id} className="card-surface rounded-xl p-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-white text-sm truncate">{u.name} <span className="text-[#C9A84A] text-xs uppercase ml-1">{u.role}</span></div>
            <div className="text-slate-500 text-xs truncate">{u.email} · 2FA {u.totp_enabled ? "on" : "off"}</div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => reset2fa(u.id)} className="text-xs card-surface border border-[#1E294B] rounded-full px-3 py-1.5 text-white">Reset 2FA</button>
            <button onClick={() => resetPw(u.id)} className="text-xs card-surface border border-[#1E294B] rounded-full px-3 py-1.5 text-white">Reset PW</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/audit").then(({ data }) => setLogs(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-2">
      {logs.length === 0 && <p className="text-slate-500 text-sm">No activity recorded yet.</p>}
      {logs.map((l) => (
        <div key={l.id} className="card-surface rounded-xl p-3 flex items-start gap-3">
          <ScrollText className="text-[#C9A84A] shrink-0 mt-0.5" size={16} />
          <div className="min-w-0">
            <div className="text-white text-sm">{l.action.replace(/_/g, " ")}{l.detail ? <span className="text-slate-400"> · {l.detail}</span> : ""}</div>
            <div className="text-slate-500 text-xs">{l.user_email || "system"} · {fmtDate(l.created_at)} {new Date(l.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} {l.ip ? `· ${l.ip}` : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ExportsTab() {
  const items = [
    { label: "Clients", file: "export/clients.csv" },
    { label: "Deadlines", file: "export/deadlines.csv" },
    { label: "Revenue", file: "export/revenue.csv" },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {items.map((i) => (
        <a key={i.file} href={downloadUrl(i.file)} data-testid={`export-${i.label.toLowerCase()}`}
          className="card-surface rounded-2xl p-5 hover:bg-[#18243F] transition-colors text-center">
          <Download className="mx-auto text-[#C9A84A] mb-2" size={24} />
          <div className="text-white font-medium">{i.label}</div>
          <div className="text-slate-500 text-xs">Download CSV</div>
        </a>
      ))}
    </div>
  );
}
